import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import { useConsumerSelector, useIdentityPassportCountriesQuery, useIdentityPassportMutation, useIsPartialVerified } from '../../state'
import { useTranslation } from '../../utils/convergence'
import { getFormSchema, SchemaUpdates } from './utils'
import { Country, ErrorCode } from '@afterpaytouch/portal-api/types'
import { Channel, IdentityDocumentVerificationStatus, IdentityPassportRequest } from '@afterpaytouch/portal-api/identity/types'
import { Message } from '@afterpaytouch/core'
import { CTAButton } from './CTAButton'
import { passportPattern } from './validations'
import FormBuilder from '@afterpaytouch/form'
import { FormWrapper } from '../FormWrapper'
import { getIdentityVerifyErrorCode, IdentityErrorCode } from '@afterpaytouch/portal-api/identity'
import { DocumentProps } from '../../model'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { isDifferentWithProfileDOB, isDifferentWithProfileNames, IsRetryPageContext, useSetErrorsAndLogEvent } from '../VerifyIdentity/helper'
import { compareObjects } from '../../utils/comparison'
import { USER_VERIFICATION_ERRORS } from '../VerifyIdentity/constants'
import { DisabledModal } from '../DisabledModal'
import { Terms } from './Terms'
import { HardIdProfileCard, ErrorMessage, HardIdHelpLink, HardIdHeader } from '../VerifyIdentity/components'
import { joinStrings } from '@afterpay/utils'

export interface PassportFormData {
  givenNames: string
  otherGivenNames: string
  surname: string
  nameAlert?: string
  passportNumber: string
  dateOfBirth?: string
  dobAlert?: string
  countryOfIssue?: string
  expiryDate?: string
  terms?: string
  footer?: string
  profileCard?: string
}

const getRequest = (data: PassportFormData, initialFormData: PassportFormData, accountCountry?: Country): IdentityPassportRequest => {
  const { countryOfIssue, passportNumber, givenNames, otherGivenNames, surname, dateOfBirth, expiryDate } = data
  return {
    channel: Channel.CHECKOUT,
    givenNames: givenNames ?? initialFormData.givenNames,
    otherGivenNames: otherGivenNames ?? initialFormData.otherGivenNames,
    surname: surname ?? initialFormData.surname,
    countryCode: (countryOfIssue as Country) ?? accountCountry ?? Country.NZ,
    number: passportNumber,
    dateOfBirth: dateOfBirth ?? initialFormData.dateOfBirth ?? '',
    expiryDate: expiryDate ?? '',
  }
}

export const Passport: FunctionComponent<DocumentProps> = ({ onSuccess = () => {}, onFailure = () => {} }) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const consumer = useConsumerSelector()
  const { accountCountry, profile } = consumer
  const { t } = useTranslation()
  const profileDOB = profile?.dateOfBirth ?? ''
  const [schemaUpdates, setSchemaUpdates] = useState<SchemaUpdates>({
    passportOptions: {},
    permittedCountries: {},
    showProfileDobAlert: false,
    showProfileNameAlert: false,
  })

  const { data: permittedCountries } = useIdentityPassportCountriesQuery()
  const [identityVerifyPassport] = useIdentityPassportMutation()
  const [submittedRequest, setSubmittedRequest] = useState<IdentityPassportRequest>()

  const [lock, setLock] = useState(false)
  const [error, setError] = useState<IdentityErrorCode | null>(null)
  const isPartialVerified = useIsPartialVerified()
  const isRetryPage = useContext(IsRetryPageContext)
  const isProfileEditable = !(isPartialVerified && isRetryPage)
  const verifyButtonAnalytics =
    isPartialVerified && isRetryPage ? TrackingEvent.PRESSED_HARDID_RETRY_PAGE_VERIFY_BUTTON : TrackingEvent.PRESSED_HARDID_PASSPORT_PAGE_VERIFY_BUTTON
  const setErrorsAndLogEvent = useSetErrorsAndLogEvent(setError, setLock, TrackingEvent.PASSPORT_SUBMIT_FAILED)

  const { schema, initialFormData } = useMemo(() => {
    const onBlur = (): void => {
      const { givenNames, otherGivenNames, surname } = formData
      const fullName = joinStrings([givenNames, otherGivenNames, surname])

      setSchemaUpdates((currSchema) => ({
        ...currSchema,
        showProfileNameAlert: isDifferentWithProfileNames(fullName, profile),
      }))
    }

    const components = {
      profileCard: () => <HardIdProfileCard />,
      nameAlert: () => (
        <Message
          kind='Success'
          iconName='Profile'
          heading={t('verifyIdentity:alert:profileUpdateHeading')}
          description={t('verifyIdentity:alert:profileUpdateName')}
          testNameSpace='verify-passport-profile-update'
        />
      ),
      dobAlert: () => (
        <Message
          kind='Success'
          iconName='Profile'
          heading={t('verifyIdentity:alert:profileUpdateHeading')}
          description={t('verifyIdentity:alert:profileUpdateDOB')}
          testNameSpace='verify-passport-dob-update'
        />
      ),
      terms: () => <Terms />,
      footer: () => <CTAButton lock={lock} submitted={lock} />,
    }
    const isUserFormError = USER_VERIFICATION_ERRORS.includes(error as IdentityErrorCode)
    return getFormSchema({ consumer, permittedCountries, components, t, onBlur, schemaUpdates, error: isUserFormError, isProfileEditable })
  }, [consumer, t, schemaUpdates, lock, permittedCountries, error, isProfileEditable, profile])

  const [passportCountry, setPassportCountry] = useState<string>()
  const [formData, setFormData] = useState(initialFormData)

  useEffect(() => {
    setSchemaUpdates((currentSchema) => ({
      ...currentSchema,
      permittedCountries,
    }))
  }, [permittedCountries])

  useEffect(() => {
    if (typeof error !== 'undefined') {
      window?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }
  }, [error])

  const onChange = (e: PassportFormData): void => {
    setError(null)
    const { countryOfIssue, dateOfBirth = initialFormData.dateOfBirth } = e
    let updatedPassportOptions = {}
    if (accountCountry !== Country.NZ && countryOfIssue !== passportCountry) {
      setPassportCountry(countryOfIssue)

      updatedPassportOptions = {
        ...schema.passportNumber.options,
        ...passportPattern({ countryOfIssue, t }),
      }
    }

    setSchemaUpdates((currentSchema) => ({
      ...currentSchema,
      passportOptions: updatedPassportOptions,
      showProfileDobAlert: isDifferentWithProfileDOB(dateOfBirth, profileDOB),
    }))

    setFormData(e)
  }

  const onFormSubmit = async (data: PassportFormData): Promise<void> => {
    setErrorsAndLogEvent({ lock: true, event: verifyButtonAnalytics })
    const request: IdentityPassportRequest = getRequest(data, initialFormData, accountCountry)

    if (compareObjects(request, submittedRequest)) {
      setErrorsAndLogEvent({ error: IdentityErrorCode.IdentityAlreadyRejected, lock: false })
      return
    }

    try {
      const response = await identityVerifyPassport(request).unwrap()
      if (response.status === IdentityDocumentVerificationStatus.VERIFIED) {
        onSuccess()
      } else if (response.status === IdentityDocumentVerificationStatus.REJECTED) {
        setSubmittedRequest(request)
        setErrorsAndLogEvent({ error: IdentityErrorCode.IdentityCheckRejected, lock: false })
      }
    } catch (e) {
      const error = getIdentityVerifyErrorCode(e)
      if ([ErrorCode.MaxAttemptsReached, ErrorCode.MaxAttemptsReachedForType].includes(e?.data?.errorCode)) {
        logEvent(TrackingEvent.PASSPORT_SUBMIT_FAILED, { idvError: e?.data?.errorCode })
        return onFailure()
      }
      setErrorsAndLogEvent({ error: error ?? IdentityErrorCode.Default, lock: false })
    }
  }

  return (
    <>
      <HardIdHeader documentType='passport' />
      <ErrorMessage identityType='passport' error={error} testNameSpace={'verify-password-error-message'} />
      <FormWrapper>
        <FormBuilder schema={schema} testNameSpace={'verify-passport'} onChange={onChange} onSubmit={onFormSubmit} disabled={lock} />
      </FormWrapper>
      <HardIdHelpLink />
      {lock && <DisabledModal />}
    </>
  )
}
